import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** 'destructive' adds red button styling for delete actions */
    variant?: 'default' | 'destructive';
    onConfirm: () => void;
}

/**
 * DS: Reusable confirmation dialog wrapping shadcn AlertDialog.
 * Replaces ad-hoc AlertDialog blocks used for delete confirmations.
 *
 * @example
 * <ConfirmDialog
 *   open={!!deleteId}
 *   onOpenChange={(open) => !open && setDeleteId(null)}
 *   title="Delete Roadmap?"
 *   description="This cannot be undone."
 *   confirmLabel="Delete"
 *   variant="destructive"
 *   onConfirm={() => handleDelete(deleteId!)}
 * />
 */
export function ConfirmDialog({
    open,
    onOpenChange,
    title = 'Are you sure?',
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'default',
    onConfirm,
}: ConfirmDialogProps) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="border-2 border-black shadow-brutal">
                <AlertDialogHeader>
                    <AlertDialogTitle className="font-black uppercase">{title}</AlertDialogTitle>
                    {description && (
                        <AlertDialogDescription>{description}</AlertDialogDescription>
                    )}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel className="border-2 border-black font-bold uppercase text-xs">
                        {cancelLabel}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        className={
                            variant === 'destructive'
                                ? 'bg-red-600 hover:bg-red-700 text-white font-bold uppercase text-xs border-2 border-red-700'
                                : 'bg-black hover:bg-gray-800 text-white font-bold uppercase text-xs border-2 border-black shadow-brutal-orange hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-brutal-orange-hover transition-all'
                        }
                    >
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
